import { EventHandler, EventPayload } from './types';

class EventManager {
  private static _instance: EventManager | null = null;

  private handlers: Map<string, Map<string, EventHandler[]>>;
  private eventListeners: Map<string, (event: Event) => void>;

  private constructor() {
    this.handlers = new Map();
    this.eventListeners = new Map();

    // Automatically clean up listeners on unload
    window.addEventListener('beforeunload', () => this.dispose());
    window.addEventListener('pagehide', () => this.dispose());
  }

  private static get router(): EventManager {
    if (!EventManager._instance) {
      EventManager._instance = new EventManager();
    }
    return EventManager._instance;
  }

  // Public methods

  static registerListener(
    eventName: string,
    type: string,
    handler: EventHandler,
  ): () => void {
    return EventManager.router._on(eventName, type, handler);
  }

  static unregisterListener(
    eventName: string,
    type: string,
    handler: EventHandler,
  ): void {
    EventManager.router._off(eventName, type, handler);
  }

  /**
   * @param targetOrigin - Required for `'message'` events: an explicit origin
   *   keeps the payload from being broadcast to whatever document happens to
   *   occupy the target frame. Ignored on the CustomEvent path.
   */
  static emit<T = EventPayload>(
    target: Window | Document | HTMLElement,
    eventName: string,
    type: string,
    payload: T,
    targetOrigin: string,
  ): void {
    EventManager.router._emit(target, eventName, type, payload, targetOrigin);
  }

  // Internal methods

  private _on(
    eventName: string,
    type: string,
    handler: EventHandler,
  ): () => void {
    if (!this.handlers.has(eventName)) {
      this.handlers.set(eventName, new Map());
      this._listen(eventName);
    }

    const typeMap = this.handlers.get(eventName)!;
    if (!typeMap.has(type)) {
      typeMap.set(type, []);
    }
    typeMap.get(type)!.push(handler as EventHandler);

    return () => this._off(eventName, type, handler); // Return unregister function
  }

  private _off(eventName: string, type: string, handler: EventHandler): void {
    if (!this.handlers.has(eventName)) return;

    const typeMap = this.handlers.get(eventName)!;
    if (!typeMap.has(type)) return;

    typeMap.set(
      type,
      typeMap.get(type)!.filter((h) => h !== handler),
    );

    if (typeMap.get(type)!.length === 0) {
      typeMap.delete(type);
    }
    if (typeMap.size === 0) {
      this.handlers.delete(eventName);
      this._unlisten(eventName);
    }
  }

  private _emit<T = EventPayload>(
    target: Window | Document | HTMLElement,
    eventName: string,
    type: string,
    payload: T,
    targetOrigin: string,
  ): void {
    if (eventName === 'message' && 'postMessage' in target) {
      (target as Window).postMessage({ type, payload }, targetOrigin);
    } else {
      const customEvent = new CustomEvent(eventName, {
        detail: { type, payload },
      });
      (target as Document | HTMLElement).dispatchEvent(customEvent);
    }
  }

  private _listen(eventName: string): void {
    if (this.eventListeners.has(eventName)) return;
    const boundHandler = (event: Event) => this._dispatch(event, eventName);
    window.addEventListener(eventName, boundHandler as EventListener);
    this.eventListeners.set(eventName, boundHandler);
  }

  private _unlisten(eventName: string): void {
    const boundHandler = this.eventListeners.get(eventName);
    if (!boundHandler) return;
    window.removeEventListener(eventName, boundHandler as EventListener);
    this.eventListeners.delete(eventName);
  }

  private _dispatch(event: Event, eventName: string): void {
    let data: Record<string, unknown> | undefined;
    if (eventName === 'message') {
      const messageEvent = event as MessageEvent;
      data = messageEvent.data;
    } else {
      const customEvent = event as CustomEvent;
      data = customEvent.detail;
    }

    const { type } = data || {};

    if (!this.handlers.has(eventName)) return;

    const typeMap = this.handlers.get(eventName)!;

    // 1. Normal handler for specific type
    if (type && typeMap.has(type as string)) {
      for (const handler of typeMap.get(type as string)!) {
        try {
          handler(event);
        } catch (err) {
          console.error(`Error in handler for [${eventName}:${type}]`, err);
        }
      }
    }

    // 2. Wildcard handler for all types or no-type
    if (typeMap.has('*')) {
      for (const handler of typeMap.get('*')!) {
        try {
          handler(event);
        } catch (err) {
          console.error(`Error in handler for [${eventName}:*]`, err);
        }
      }
    }
  }

  // Cleanup method to remove all listeners on unload
  dispose(): void {
    for (const [eventName, boundHandler] of this.eventListeners) {
      window.removeEventListener(eventName, boundHandler as EventListener);
    }
    this.eventListeners.clear();
    this.handlers.clear();
    console.log('[EventRouter]: All listeners removed.');
  }
}

export default EventManager;
